import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useApi } from "../lib/api";
import { fmtUsd, fmtNum, timeAgo, shortAddr } from "../lib/format";
import { CopyBtn, WatchStar, DataBadge, SectionHead, Skel, Empty, AddrLink } from "../components/common";
import usePageMeta from "../lib/usePageMeta";
import { BLOCKSCOUT_URL } from "../lib/wallet";

const TABS = ["Holdings", "Transactions", "Token Transfers"];

const Stat = ({ label, value, cls = "" }) => (
  <div className="t-card p-3"><div className="text-[10px] uppercase text-[#6B7280]">{label}</div><div className={`num text-sm mt-0.5 ${cls}`}>{value}</div></div>
);

const COLORS = ["#059669", "#60A5FA", "#F59E0B", "#A78BFA", "#EF4444", "#10B981", "#F97316", "#3F3F46"];

export default function WalletDetail() {
  const { address } = useParams();
  usePageMeta(`Wallet ${address?.slice(0, 10)}…`, "Live Robinhood Chain wallet balances, token holdings, transactions and transfers.");
  const { data, error } = useApi(`/explorer/address/${address}`);
  const { data: stats } = useApi("/explorer/stats");
  const [tab, setTab] = useState(TABS[0]);

  if (error) return <Empty msg="Invalid wallet address" />;
  if (!data) return <Skel rows={10} h={44} />;
  if (!data.live) return <Empty msg="This address was not found on Robinhood Chain, or live data is temporarily unavailable." />;

  const info = data.info || {};
  const ethPrice = parseFloat(stats?.data?.coin_price || 0);
  const ethBal = parseInt(info.coin_balance || "0") / 1e18;
  const holdings = (data.token_balances || []).map((b) => {
    const dec = parseInt(b.token?.decimals) || 18;
    const bal = parseInt(b.value || "0") / 10 ** dec;
    const price = b.token?.exchange_rate ? parseFloat(b.token.exchange_rate) : null;
    return {
      symbol: b.token?.symbol || "?", name: b.token?.name || "Unknown token",
      address: b.token?.address_hash || b.token?.address, icon: b.token?.icon_url,
      balance: bal, price, usd: price != null ? bal * price : null,
      isStock: (b.token?.name || "").includes("Robinhood Token"),
    };
  }).sort((a, b) => (b.usd || 0) - (a.usd || 0));
  const tokenUsd = holdings.reduce((a, h) => a + (h.usd || 0), 0);
  const nativeUsd = ethPrice ? ethBal * ethPrice : 0;
  const total = tokenUsd + nativeUsd;
  const pie = [
    ...(nativeUsd > 0 ? [{ name: "ETH", value: nativeUsd }] : []),
    ...holdings.filter((h) => h.usd > 0).slice(0, 7).map((h) => ({ name: h.symbol, value: h.usd })),
  ];

  return (
    <div className="space-y-4">
      <div className="t-card p-4" data-testid="wallet-header">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-bold text-lg num">{shortAddr(address, 8)}</h1>
              <CopyBtn text={address} />
              {info.name && <span className="text-xs text-[#10B981] border border-[#05966940] px-1.5 py-0.5 rounded-sm">{info.name}</span>}
              {info.is_contract && <span className="text-[10px] uppercase text-[#F59E0B] border border-[#F59E0B40] px-1.5 py-0.5 rounded-sm">Contract</span>}
              <WatchStar type="wallet" id={address} label={info.name || shortAddr(address)} sub="wallet" />
            </div>
            <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-2"><DataBadge live />
              <a href={`${BLOCKSCOUT_URL}/address/${address}`} target="_blank" rel="noreferrer" className="hover:text-[#10B981]">View on Blockscout ↗</a>
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase text-[#6B7280]">Estimated Portfolio Value</div>
            <div className="num text-2xl font-semibold" data-testid="wallet-total-value">{fmtUsd(total)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="wallet-stats">
        <Stat label="ETH Balance" value={`${ethBal.toLocaleString(undefined, { maximumFractionDigits: 6 })} ETH`} />
        <Stat label="ETH Value" value={ethPrice ? fmtUsd(nativeUsd) : "—"} />
        <Stat label="Token Holdings Value" value={fmtUsd(tokenUsd)} />
        <Stat label="Token Positions" value={holdings.length} />
      </div>

      {pie.length > 0 && (
        <div className="t-card">
          <div className="px-3 py-2 border-b border-[#262626] text-xs uppercase text-[#6B7280]">Portfolio Allocation (priced assets)</div>
          <div className="h-52 p-2" data-testid="portfolio-allocation-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none">
                  {pie.map((d, i) => <Cell key={d.name} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", borderRadius: 2, fontSize: 12 }} formatter={(v, n) => [fmtUsd(v), n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex gap-px border-b border-[#262626] overflow-x-auto">
        {TABS.map((tb) => (
          <button key={tb} data-testid={`wallet-tab-${tb.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setTab(tb)}
            className={`text-[13px] px-3.5 py-2 border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === tb ? "border-[#059669] text-[#F3F4F6]" : "border-transparent text-[#6B7280] hover:text-[#9CA3AF]"}`}>{tb}</button>
        ))}
      </div>

      {tab === "Holdings" && (
        <div className="t-card overflow-x-auto">
          {!holdings.length ? <Empty msg="No token holdings" /> : (
            <table className="w-full t-table" data-testid="positions-table">
              <thead><tr className="text-left border-b border-[#262626]"><th>Token</th><th className="text-right">Balance</th><th className="text-right">Price</th><th className="text-right">USD Value</th></tr></thead>
              <tbody>
                {holdings.map((h, i) => (
                  <tr key={i}>
                    <td>
                      <Link to={`/token/${h.address}`} className="flex items-center gap-2 hover:text-[#10B981]">
                        <span className="font-medium">{h.symbol}</span>
                        {h.isStock && <span className="text-[9px] uppercase text-[#F59E0B] border border-[#F59E0B40] px-1 rounded-sm">Stock</span>}
                        <span className="text-xs text-[#6B7280] truncate max-w-[240px]">{h.name}</span>
                      </Link>
                    </td>
                    <td className="text-right num text-[#9CA3AF]">{fmtNum(h.balance)}</td>
                    <td className="text-right num text-[#6B7280]">{h.price != null ? fmtUsd(h.price) : "—"}</td>
                    <td className="text-right num">{h.usd != null ? fmtUsd(h.usd) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "Transactions" && (
        <div className="t-card overflow-x-auto">
          {!data.transactions?.length ? <Empty msg="No transactions" /> : (
            <table className="w-full t-table" data-testid="wallet-transactions-table">
              <thead><tr className="text-left border-b border-[#262626]"><th>Hash</th><th>Method</th><th>From</th><th>To</th><th className="text-right">Value</th><th className="text-right">Time</th></tr></thead>
              <tbody>
                {data.transactions.map((tx) => (
                  <tr key={tx.hash}>
                    <td><Link to={`/explorer/tx/${tx.hash}`} className="num text-xs text-[#10B981] hover:underline">{shortAddr(tx.hash, 6)}</Link></td>
                    <td className="text-xs text-[#9CA3AF]">{tx.method || "transfer"}</td>
                    <td><AddrLink addr={tx.from?.hash} /></td>
                    <td><AddrLink addr={tx.to?.hash} /></td>
                    <td className="text-right num text-xs">{(parseInt(tx.value || "0") / 1e18).toFixed(5)} ETH</td>
                    <td className="text-right num text-xs text-[#6B7280]">{tx.timestamp ? timeAgo(new Date(tx.timestamp).getTime() / 1000) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "Token Transfers" && (
        <div className="t-card overflow-x-auto">
          {!data.token_transfers?.length ? <Empty msg="No token transfers" /> : (
            <table className="w-full t-table" data-testid="wallet-transfers-table">
              <thead><tr className="text-left border-b border-[#262626]"><th>Time</th><th>Token</th><th>From</th><th>To</th><th className="text-right">Amount</th><th>Tx</th></tr></thead>
              <tbody>
                {data.token_transfers.map((t, i) => {
                  const dec = parseInt(t.token?.decimals) || 18;
                  const amt = t.total?.value ? parseInt(t.total.value) / 10 ** dec : null;
                  return (
                    <tr key={i}>
                      <td className="num text-xs text-[#6B7280]">{t.timestamp ? timeAgo(new Date(t.timestamp).getTime() / 1000) : "—"}</td>
                      <td><Link to={`/token/${t.token?.address_hash || t.token?.address}`} className="font-medium hover:text-[#10B981]">{t.token?.symbol || "?"}</Link></td>
                      <td><AddrLink addr={t.from?.hash} /></td>
                      <td><AddrLink addr={t.to?.hash} /></td>
                      <td className="text-right num text-[#9CA3AF]">{amt != null ? fmtNum(amt) : "—"}</td>
                      <td><Link to={`/explorer/tx/${t.transaction_hash}`} className="num text-xs text-[#10B981] hover:underline">{shortAddr(t.transaction_hash, 4)}</Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
